import isUndefined from 'lodash/isUndefined';
import isEqual from 'lodash/isEqual';
import size from 'lodash/size';
import React, {Fragment, useState, useRef} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import loadable from '@loadable/component';
import classNames from 'classnames';
import IconFilter from '@spothero/icons/filter';
import {Box, Button, Icon} from '@spothero/ui';
import {Badge} from '@spothero/ui-backlog';
import AmenityUtils from '@spothero/utils/amenity';
import {getSpotsV2} from 'store/spots/spots-selectors';
import {resetFilters, updateFilters} from 'store/filters/filters-actions';
import {
    getAmenities,
    getReservationTypes,
} from 'store/filters/filters-selectors';
import trackProductListFiltered from 'segment/events/product-list-filtered';
import FilterItem from './filter-item/FilterItem';

const AvailabilityFilters = loadable(() =>
    import('./monthly-options-filter/AvailabilityFilters')
);
const FiltersModal = loadable(() => import('./filters-modal/FiltersModal'));

const buttonColor = isCondensed => ({
    backgroundColor: isCondensed ? 'white' : 'transparent',
});

const QuickFilters = ({
    className,
    showReservationTypes,
    visibleSlugs,
    extraFilterSlot,
    isMobile,
    isCondensed,
    buttonText = '',
}) => {
    const dispatch = useDispatch();
    const amenitiesSelectedOnModalOpen = useRef();
    const [showModal, setShowModal] = useState(false);
    const {
        filteredSpotsNum: numSpots,
        numActive: numActiveFilters,
        currentAmenities: activeAmenities,
        allAmenities: amenities,
        resTypes: reservationTypes,
        newActiveReservationType: activeReservationType,
    } = useSelector(state => {
        // getReservationTypes is only applicable for monthly
        const resTypes = getReservationTypes(state);
        const allAmenities = getAmenities(state);

        const {
            filters: {
                numActive,
                activeAmenities: currentAmenities,
                activeReservationType: activeResType,
            },
        } = state;
        let newActiveReservationType = activeResType;

        // check if reservation type is valid
        if (
            newActiveReservationType !== 'all' &&
            isUndefined(resTypes[newActiveReservationType])
        ) {
            newActiveReservationType = 'all';
        }

        const filteredSpotsNum = getSpotsV2(state).length;

        return {
            numActive,
            allAmenities,
            currentAmenities,
            resTypes,
            newActiveReservationType,
            filteredSpotsNum,
        };
    });
    const storeState = useSelector(state => state);
    const badgeLabel =
        numActiveFilters > 0 ? numActiveFilters.toString() : null;
    const classes = classNames('QuickFilters', className);
    const trackFilterChange = (newFilters = {}) => {
        trackProductListFiltered({
            state: storeState,
            filters: {
                ...storeState.filters,
                ...newFilters,
            },
        });
    };
    const onAmenityChange = (amenity, isChecked, isQuickFilter = false) => {
        let newActiveAmenities = [...activeAmenities];

        if (isChecked) {
            newActiveAmenities.push(amenity);
        } else {
            newActiveAmenities = newActiveAmenities.filter(
                item => item !== amenity
            );
        }

        const changedFilters = {
            activeAmenities: newActiveAmenities,
            viaQuickFilter: isQuickFilter,
        };
        const isTrackChange = isQuickFilter && !showModal;

        dispatch(updateFilters(changedFilters));

        if (isTrackChange) {
            trackFilterChange(changedFilters);
        }
    };
    const onClearFilters = () => {
        dispatch(resetFilters());
    };
    const onReservationTypeChange = value => {
        const changedFilters = {
            activeReservationType: value,
        };

        dispatch(updateFilters(changedFilters));

        trackFilterChange(changedFilters);
    };
    const onModalToggle = () => {
        let isTrackChange = false;

        // If modal is being closed and filters have been changed since open (and clear-all hasn't been pressed), track changes
        if (showModal) {
            if (
                !isEqual(activeAmenities, amenitiesSelectedOnModalOpen.current)
            ) {
                isTrackChange = true;
            }
            amenitiesSelectedOnModalOpen.current = [];
        } else {
            amenitiesSelectedOnModalOpen.current = activeAmenities;
        }

        setShowModal(show => !show);

        if (isTrackChange) {
            trackFilterChange();
        }
    };

    return (
        <Box className={classes}>
            <Box
                className={classNames({
                    'QuickFilters-group': Boolean(extraFilterSlot),
                })}
                sx={{
                    '.FilterItem-label': buttonColor(isCondensed),
                    '.QuickFilters-filter': buttonColor(isCondensed),
                    '.FormElement-item': buttonColor(isCondensed),
                }}
            >
                {!showReservationTypes &&
                    Boolean(size(amenities)) &&
                    visibleSlugs.length > 0 && (
                        <Box className="QuickFilters-items">
                            {visibleSlugs.map(slug => {
                                const amenity = amenities[slug];

                                if (!amenity) {
                                    return null;
                                }

                                // if no filters are active and this amenity doesn't apply to the current search, hide it
                                // so that it does not show up as a disabled amenity when it really has no business being there
                                const isHidden =
                                    numActiveFilters === 0 &&
                                    amenity.count === 0;
                                const isTouchless = slug === 'touchless';
                                const touchlessId = 'QuickFilter-touchless';

                                if (isHidden) {
                                    return null;
                                }

                                return (
                                    <Fragment key={amenity.slug}>
                                        <FilterItem
                                            {...(isTouchless && {
                                                id: touchlessId,
                                            })}
                                            slug={amenity.slug}
                                            label={amenity.name}
                                            defaultChecked={activeAmenities.includes(
                                                amenity.slug
                                            )}
                                            disabled={amenity.count === 0}
                                            isQuickFilter
                                            onChange={onAmenityChange}
                                        />
                                    </Fragment>
                                );
                            })}
                        </Box>
                    )}
                {showReservationTypes && (
                    <AvailabilityFilters
                        activeReservationType={activeReservationType}
                        reservationTypes={reservationTypes}
                        onChange={onReservationTypeChange}
                    />
                )}
                {extraFilterSlot && !isMobile ? extraFilterSlot : null}
            </Box>
            <Button
                className="QuickFilters-filter"
                data-testid="QuickFilters-filter"
                onClick={onModalToggle}
                backgroundColor={isCondensed ? 'white' : 'transparent'}
                paddingY="0"
                paddingX="3"
                color="text.primary.light"
                borderColor="accent"
                fontWeight="base"
                fontSize="sm"
            >
                {badgeLabel ? (
                    <Badge className="QuickFilters-filter-badge">
                        {badgeLabel}
                    </Badge>
                ) : (
                    <Icon as={IconFilter} />
                )}
                {buttonText || 'Filter'}
            </Button>
            {extraFilterSlot && isMobile ? extraFilterSlot : null}
            {showModal && (
                <FiltersModal
                    numSpots={numSpots}
                    amenities={amenities}
                    activeAmenities={activeAmenities}
                    numActiveFilters={numActiveFilters}
                    onChange={onAmenityChange}
                    onClear={onClearFilters}
                    onHidden={onModalToggle}
                />
            )}
        </Box>
    );
};

QuickFilters.propTypes = {
    className: PropTypes.string,
    showReservationTypes: PropTypes.bool,
    isMobile: PropTypes.bool,
    visibleSlugs: PropTypes.arrayOf(
        PropTypes.oneOf(Object.keys(AmenityUtils.ICONS))
    ),
    extraFilterSlot: PropTypes.object,
    isCondensed: PropTypes.bool,
    buttonText: PropTypes.string,
};

QuickFilters.defaultProps = {
    visibleSlugs: [],
};

export default QuickFilters;
